import affirm from '../core/Affirm'
import algo from '../core/Algo'
import { AllOutputKeyType, IFlow, INode, INodeOutputContext, NodeType } from '../definitions/Nodes'
import { ExecutionEnvironmentMode } from '../definitions/NodesExecution'
import { NodeOutputMap } from '../definitions/NodeTypes'
import ValidationManager from '../managers/ValidationManager'
import TimelineEngine from './TimelineEngine'

class QuantumTimelineEngineClass {
    simulate = (flow: IFlow, mode?: ExecutionEnvironmentMode): IQuantumMatrix => {
        affirm(flow, 'Invalid flow')
        affirm(flow.nodes, 'The provided flow has null nodes')
        affirm(flow.nodes.every(algo.hasVal), 'Invalid nodes found inside flow')

        ValidationManager.validate(flow, mode)

        const quMatrix: IQuantumMatrix = { layers: [] }

        const timeline = TimelineEngine.build(flow)

        for (let i = 0; i < timeline.steps.length; i++) {
            const step = timeline.steps[i]
            const prevStep = timeline.steps[i - 1]

            let newQuLayer: IQuantumLayer = { depth: step.depth, superpositions: [] }
            const prevQuLayer = quMatrix.layers[i - 1]

            const forks = prevStep?.nodes.filter(x => x.type === 'fork') ?? []

            if (forks.length === 0) {
                this.addSuperposition(newQuLayer, step.nodes, prevQuLayer)
            } else {
                const futureBranches: IAlternateBranch[] = []

                const nodesIndependentFromForks = step.nodes.filter(x => x.inputs.some(k => prevQuLayer.superpositions
                    .filter(s => !s.nodes.some(x => x.type === 'fork'))
                    .flatMap(s => s.nodes).some(z => z.uuid === k.uuid)))

                const nodesDependentFromForks = step.nodes.filter(x => x.inputs.some(k => prevQuLayer.superpositions
                    .filter(s => s.nodes.some(x => x.type === 'fork'))
                    .flatMap(s => s.nodes).filter(n => n.type !== 'fork').some(n => n.uuid === k.uuid)))

                for (const fork of forks) {
                    const possiblePositions: { [key: string]: INode[] } = {}
                    const paths = step.nodes.filter(x => x.inputs.some(k => k.uuid === fork.uuid))
                    for (const path of paths) {
                        const branch = path.inputs.find(x => x.uuid === fork.uuid).outputKey

                        if (!possiblePositions[branch]) possiblePositions[branch] = []
                        possiblePositions[branch].push(path)
                    }

                    futureBranches.push({
                        fork,
                        realities: Object.keys(possiblePositions)
                            .map(x => ({ measurement: x, nodes: possiblePositions[x] }))
                    })
                }

                const allPermutations = this.permutateSuperpositions(futureBranches, nodesDependentFromForks)

                allPermutations.forEach(x => {
                    this.addSuperposition(newQuLayer, x.flatMap(k => k.nodes).filter(algo.hasVal), prevQuLayer, this.quMeasurementSignature(x.flatMap(k => k.measurement).filter(algo.hasVal)))
                })

                if (nodesIndependentFromForks.length > 0)
                    this.addSuperposition(newQuLayer, nodesIndependentFromForks, prevQuLayer)
            }

            newQuLayer = { ...newQuLayer, superpositions: newQuLayer.superpositions.filter(s => s.nodes.length > 0) }
            quMatrix.layers.push(newQuLayer)
        }

        return quMatrix
    }

    collapse = (currQuPositions: IQuantumPosition[], prevQuPosition: IQuantumPosition, outputs: INodeOutputContext<NodeType>[]): IQuantumPosition => {
        if (!algo.hasVal(prevQuPosition)) return currQuPositions[0]
        let position: IQuantumPosition = null

        const isInitialPosition = currQuPositions.some(c => !algo.hasVal(c.prevSuperposition) && !algo.hasVal(c.measurement))
        const hasPrevSuperposition = currQuPositions.some(c => algo.hasVal(c.prevSuperposition) && !algo.hasVal(c.measurement))
        const hasMeasurement = currQuPositions.some(c => algo.hasVal(c.measurement))

        if (isInitialPosition) {
            position = currQuPositions[0]
        }
        if (hasPrevSuperposition) {
            position = currQuPositions.find(currS => currS.prevSuperposition === prevQuPosition.signature)
        }
        if (hasMeasurement) {
            const forkOutputs = outputs.filter(x => x.node.type === 'fork').map(x => x.output) as NodeOutputMap['fork'][]
            const measurements = forkOutputs.map(fork => Object.keys(fork).find(k => fork[k] !== null) as AllOutputKeyType)
            const measurementKey = this.quMeasurementSignature(measurements)
            position = currQuPositions.find(currS => currS.measurement === measurementKey)
        }
        return position
    }

    quSignature = (nodes: INode[]) => nodes.map(x => x.uuid).sort().join('_')

    quMeasurementSignature = (measurements: string[]) => {
        if (measurements.length === 1) return measurements[0]
        return measurements.length ? measurements.join('_').replace(/_$/, '') : null
    }

    print = (quMatrix: IQuantumMatrix) => {
        for (const layer of quMatrix.layers) {
            console.log('Layer', layer.depth, `(${layer.superpositions.length})`)
            for (const position of layer.superpositions) {
                console.log('   Superposition|', position.signature, position.prevSuperposition ? `Prev: ${position.prevSuperposition}` : '', position.measurement ? `Measurement: ${position.measurement}` : '')
                console.log('       Nodes:', position.nodes.map(x => x.name).join(', '))
            }
            console.log()
        }
    }

    /**
     * Given a list of possible futureBranches created by the presence of forks,
     * return all the various permutations of realities that could be real.
     * This is important when a single layer has multiple forks.
     * e.g.
     *  Fork 1 -> A | B
     *  Fork 2 -> C | D
     * realities:
     *  A, C
     *  A, D
     *  B, C
     *  B, D
     * At runtime, only one of those realities can be real, the measure function will pick the correct superposition.
     */
    private permutateSuperpositions = (futureBranches: IAlternateBranch[], fixedNodes: INode[]): IQuantumReality[][] => {
        if (futureBranches.length === 0) return [[...fixedNodes.map(node => ({ measurement: null, nodes: [node] }))]]

        const [first, ...rest] = futureBranches
        const permutationsRest = this.permutateSuperpositions(rest, fixedNodes)

        return first.realities.flatMap(branch =>
            permutationsRest.map(perm => [branch, ...perm])
        )
    }

    /**
     * WARNING: this function is not pure, it modifies the "layer" parameter
     * Adds the nodes to the quantum layer.
     * If the previous layer has more than one superposition, then only the nodes that possible for each superposition are added.
     * e.g.
     *  Fork 1 -> A | B
     *  A -> C
     *  B -> D
     * result:
     *  Fork 1 creates two superpositions, A and B, then when I'm adding the nodes C and D in the next layer,
     *  only add C to the superposition A; and D to the superposition B
     */
    private addSuperposition = (layer: IQuantumLayer, nodes: INode[], prevQuLayer: IQuantumLayer, measurement?: string) => {
        if (!prevQuLayer) {
            layer.superpositions.push({
                nodes: nodes,
                signature: this.quSignature(nodes),
                measurement: measurement ?? null
            })
        } else {
            for (const superposition of prevQuLayer.superpositions) {
                const collapsedNodes = this.simulateSuperposition(superposition, nodes)
                layer.superpositions.push({
                    nodes: collapsedNodes,
                    signature: this.quSignature(collapsedNodes),
                    prevSuperposition: prevQuLayer.superpositions.length > 1 ? superposition.signature : null,
                    measurement: measurement ?? null
                })
            }
        }
    }

    /**
     * Exploring this superposition, means only getting the nodes that can be executed
     * if the matrix collapses to this position, so only the nodes that are descendants
     * from nodes in this superposition
     */
    private simulateSuperposition = (quPosition: IQuantumPosition, nodes: INode[]) => {
        const collapsedNodes = nodes.filter(x => x.inputs.some(k => quPosition.nodes.some(z => z.uuid === k.uuid)))
        return collapsedNodes
    }
}

const QuantumTimelineEngine = new QuantumTimelineEngineClass()
export default QuantumTimelineEngine

export interface IQuantumMatrix {
    layers: IQuantumLayer[]
}

export interface IQuantumLayer {
    depth: number
    superpositions: IQuantumPosition[]
}

export interface IQuantumPosition {
    signature: string
    nodes: INode[]
    prevSuperposition?: string
    measurement?: string
}

interface IQuantumReality {
    measurement: string
    nodes: INode[]
}

interface IAlternateBranch {
    fork: INode
    realities: IQuantumReality[]
}
